import { create } from "zustand";
import type { StudentListRow } from "@/features/students/types/student.types";
import type { ListFilterValues } from "@/shared/components/common/ListFilterPopover";

export const initialStudentListFilters: ListFilterValues = {
  board: "",
  class: "",
  section: "",
  /** Institute dropdown next to the search box; "" = all institutes. */
  institute_id: "",
  accountStatus: "",
};

export interface StudentsUploadState {
  isUploading: boolean;
  progress: number;
  fileName: string;
  fileSize: string;
  error: string | null;
}

export interface StudentsSuccessNotification {
  message: string;
  count?: number;
}

export interface StudentsListState {
  list: StudentListRow[];
  uploadState: StudentsUploadState | null;
  successNotification: StudentsSuccessNotification | null;
  page: number;
  totalCount: number;
  listFilterValues: ListFilterValues;
  searchQuery: string;

  setPage: (page: number) => void;
  setTotalCount: (count: number) => void;
  setList: (list: StudentListRow[]) => void;
  setListFilterValues: (values: ListFilterValues) => void;
  /** Also resets the page to 1. */
  setSearchQuery: (query: string) => void;
  setUploadState: (state: StudentsUploadState | null) => void;
  setSuccessNotification: (
    notification: StudentsSuccessNotification | null,
  ) => void;
  dismissSuccessNotification: () => void;
  removeStudent: (id: string) => void;
  updateStudent: (id: string, row: Omit<StudentListRow, "id">) => void;
}

export const useStudentsListStore = create<StudentsListState>((set) => ({
  list: [],
  uploadState: null,
  successNotification: null,
  page: 1,
  totalCount: 0,
  listFilterValues: initialStudentListFilters,
  searchQuery: "",

  setPage: (page) => set({ page }),
  setTotalCount: (totalCount) => set({ totalCount }),
  setList: (list) => set({ list }),
  setListFilterValues: (listFilterValues) => set({ listFilterValues }),
  setSearchQuery: (searchQuery) => set({ searchQuery, page: 1 }),
  setUploadState: (uploadState) => set({ uploadState }),
  setSuccessNotification: (successNotification) => set({ successNotification }),
  dismissSuccessNotification: () => set({ successNotification: null }),
  removeStudent: (id) =>
    set((s) => ({ list: s.list.filter((r) => r.id !== id) })),
  updateStudent: (id, row) =>
    set((s) => ({
      list: s.list.map((r) => (r.id === id ? { ...row, id } : r)),
    })),
}));
